import React, { useEffect } from "react";
import * as PopperJS from "@popperjs/core";
import { useDispatch, useSelector } from "react-redux";
import { ThunkDispatch } from "redux-thunk";
import { AnyAction } from "@reduxjs/toolkit";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Tooltip } from "~/components/Tooltip";
import { RootReduxState } from "~/modules/app/components/ReduxProvider/types";
import { fetchCatalog } from "~/modules/catalog/store";
import { faExternalLink } from "@fortawesome/pro-solid-svg-icons";
import { Anchor } from "~/components/Anchor";
import { getMeasureURL, getReferenceDataURL } from "~/routes";
import { CatalogUserEvents } from "~/modules/analytics/events/catalog";

// // // //

export function CatalogTooltipProvider(props: {
    slug: string;
    linkToCatalog?: boolean;
    placement?: PopperJS.Placement;
    analyticsEventProperties?: Record<string, string>;
    /** Rendered while the catalog loads, or when the slug isn't in it. */
    fallback?: React.ReactNode;
    children: (childProps: {
        setTriggerRef:
            | React.Dispatch<React.SetStateAction<HTMLElement | null>>
            | undefined;
    }) => React.ReactNode;
}) {
    const {
        slug,
        linkToCatalog = true,
        placement = "auto",
        analyticsEventProperties = {},
        fallback = null,
        children,
    } = props;

    const dispatch: ThunkDispatch<{}, {}, AnyAction> = useDispatch();
    const { items, loading } = useSelector(
        (state: RootReduxState) => state.catalog,
    );

    useEffect(() => {
        if (items.length === 0) {
            dispatch(fetchCatalog());
        }
    }, [dispatch, items.length]);

    if (loading) {
        return <>{fallback}</>;
    }

    const item = items.find((i) => i.slug === slug);
    if (!item) {
        return <>{fallback}</>;
    }

    // Get the URL for the catalog item
    const href =
        item.type === "metric"
            ? getMeasureURL({ measureSlug: item.slug })
            : getReferenceDataURL({ referenceSlug: item.slug });

    // Get the analytics event for the catalog item
    const analyticsEvent =
        item.type === "metric"
            ? CatalogUserEvents.CatalogTooltipClickMetricLink
            : CatalogUserEvents.CatalogTooltipClickReferenceDataLink;

    return (
        <Tooltip
            placement={placement}
            interactive={linkToCatalog}
            delayHide={linkToCatalog ? 150 : undefined}
            tooltipContent={
                <div className="px-3 py-2 max-w-sm flex flex-col gap-1 text-left">
                    <p className="font-light text-sm">{item.name}</p>
                    <p className="text-xs font-light mt-1">
                        {item.description}
                    </p>

                    {linkToCatalog && (
                        <Anchor
                            href={href}
                            openInNewTab
                            analyticsLabel={analyticsEvent}
                            analyticsEventProperties={{
                                slug: item.slug,
                                ...analyticsEventProperties,
                            }}
                            className="mt-1 inline-flex items-center gap-1.5 text-xs font-light text-blue-300 hover:text-blue-200 transition-colors"
                        >
                            Open in Data Catalog
                            <FontAwesomeIcon
                                icon={faExternalLink}
                                className="h-2.5"
                            />
                        </Anchor>
                    )}
                </div>
            }
        >
            {({ setTriggerRef }) => <>{children({ setTriggerRef })}</>}
        </Tooltip>
    );
}
